import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

interface ClientFolder {
  id: number;
  client_name: string;
  folder_path: string;
  file_count: number;
  total_size: number;
  created_at: string;
}

interface UploadFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

export const ClientFolderManager: React.FC = () => {
  const [folders, setFolders] = useState<ClientFolder[]>([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<ClientFolder | null>(null);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareExpiry, setShareExpiry] = useState(7);
  const [sharePassword, setSharePassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'folders' | 'upload' | 'share'>('folders');

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      const response = await fetch('http://localhost:8787/api/client/folders?userId=1');
      const data = await response.json();
      if (data.success) {
        setFolders(data.folders);
      }
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    setLoading(true);
    try {
      const response = await fetch('http://localhost:8787/api/client/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: 1,
          clientName: newFolderName,
        }),
      });

      const data = await response.json();
      if (data.success) {
        await fetchFolders();
        setNewFolderName('');
        alert(`Folder created: ${data.folder.url}`);
      } else {
        alert(data.error || 'Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      alert('Failed to create folder');
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file: File, folderId: number) => {
    // Request upload URL
    const requestResponse = await fetch('http://localhost:8787/api/upload/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        folderId,
        filename: file.name,
        size: file.size,
        mimeType: file.type,
        userId: 1,
      }),
    });

    const requestData = await requestResponse.json();
    if (!requestData.success) {
      throw new Error(requestData.error);
    }

    // Upload file directly
    const uploadResponse = await fetch(requestData.upload.uploadUrl, {
      method: 'PUT',
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error('Upload failed');
    }

    // Complete upload
    await fetch('http://localhost:8787/api/upload/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileId: requestData.upload.fileId,
        folderId,
        filename: file.name,
        size: file.size,
        mimeType: file.type,
        userId: 1,
      }),
    });
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!selectedFolder) {
      alert('Please select a folder first');
      return;
    }

    const newFiles: UploadFile[] = acceptedFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending',
    }));

    setUploadFiles(prev => [...prev, ...newFiles]);

    // Upload files sequentially
    newFiles.forEach(async (uploadFile, index) => {
      try {
        setUploadFiles(prev =>
          prev.map((f, i) =>
            i === index + uploadFiles.length
              ? { ...f, status: 'uploading', progress: 50 }
              : f
          )
        );

        await uploadFile(uploadFile.file, selectedFolder.id);

        setUploadFiles(prev =>
          prev.map((f, i) =>
            i === index + uploadFiles.length
              ? { ...f, status: 'completed', progress: 100 }
              : f
          )
        );
      } catch (error) {
        setUploadFiles(prev =>
          prev.map((f, i) =>
            i === index + uploadFiles.length
              ? { ...f, status: 'error', error: error.message }
              : f
          )
        );
      }
    });
  }, [selectedFolder, uploadFiles.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const shareFiles = async () => {
    if (!selectedFolder || !shareEmail) {
      alert('Please select a folder and enter recipient email');
      return;
    }

    setLoading(true);
    try {
      // For demo, we'll share all files in the folder
      const filesResponse = await fetch(
        `http://localhost:8787/api/folders/${selectedFolder.id}/files`
      );
      const filesData = await filesResponse.json();

      if (!filesData.success || filesData.files.length === 0) {
        alert('No files to share');
        return;
      }

      const fileIds = filesData.files.map(f => f.file_id);

      const response = await fetch('http://localhost:8787/api/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folderId: selectedFolder.id,
          fileIds,
          recipientEmail: shareEmail,
          expiresIn: shareExpiry,
          password: sharePassword || undefined,
          message: shareMessage || undefined,
          userId: 1,
        }),
      });

      const data = await response.json();
      if (data.success) {
        alert(`Share link created: ${data.shareLink.url}\nEmail sent to: ${shareEmail}`);
        setShareDialogOpen(false);
        setShareEmail('');
        setShareMessage('');
        setSharePassword('');
      } else {
        alert(data.error || 'Failed to create share link');
      }
    } catch (error) {
      console.error('Error sharing files:', error);
      alert('Failed to share files');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">📁 Client Folder Manager</h1>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('folders')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'folders' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            📂 Folders
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'upload' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
            disabled={!selectedFolder}
          >
            ⬆️ Upload
          </button>
          <button
            onClick={() => setActiveTab('share')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'share' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
            disabled={!selectedFolder}
          >
            🔗 Share
          </button>
        </div>

        {/* Folders Tab */}
        {activeTab === 'folders' && (
          <div className="space-y-6">
            {/* Create Folder */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Create New Folder</h2>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter client name (e.g., acme-corp)"
                  className="flex-1 px-4 py-2 bg-gray-900 rounded border border-gray-700 text-white"
                  onKeyPress={(e) => e.key === 'Enter' && createFolder()}
                />
                <button
                  onClick={createFolder}
                  disabled={!newFolderName.trim() || loading}
                  className="px-6 py-2 bg-blue-600 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Folder'}
                </button>
              </div>
            </div>

            {/* Folder List */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Your Folders</h2>
              {folders.length === 0 ? (
                <p className="text-gray-400">No folders yet. Create one to get started!</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {folders.map((folder) => (
                    <div
                      key={folder.id}
                      onClick={() => setSelectedFolder(folder)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedFolder?.id === folder.id
                          ? 'bg-blue-900/30 border-blue-500'
                          : 'bg-gray-700 border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <h3 className="font-bold text-lg mb-2">📁 {folder.client_name}</h3>
                      <p className="text-sm text-gray-400 mb-1">/{folder.folder_path}</p>
                      <div className="flex justify-between text-sm">
                        <span>{folder.file_count || 0} files</span>
                        <span>{formatFileSize(folder.total_size)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && selectedFolder && (
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">
                Upload to: {selectedFolder.client_name}
              </h2>

              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
                  isDragActive
                    ? 'border-blue-500 bg-blue-900/20'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <input {...getInputProps()} />
                <div className="text-6xl mb-4">📤</div>
                {isDragActive ? (
                  <p className="text-xl">Drop files here...</p>
                ) : (
                  <>
                    <p className="text-xl mb-2">Drag & drop files here</p>
                    <p className="text-gray-400">or click to browse</p>
                  </>
                )}
              </div>

              {/* Upload Progress */}
              {uploadFiles.length > 0 && (
                <div className="mt-6 space-y-2">
                  <h3 className="font-bold mb-2">Upload Progress:</h3>
                  {uploadFiles.map((file, index) => (
                    <div key={index} className="bg-gray-700 rounded p-3">
                      <div className="flex justify-between mb-2">
                        <span className="truncate">{file.file.name}</span>
                        <span className="text-sm">
                          {file.status === 'completed' && '✅'}
                          {file.status === 'error' && '❌'}
                          {file.status === 'uploading' && '⏳'}
                          {file.status === 'pending' && '⏸️'}
                        </span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div
                          className={`h-full rounded-full transition-all ${
                            file.status === 'completed'
                              ? 'bg-green-500'
                              : file.status === 'error'
                              ? 'bg-red-500'
                              : 'bg-blue-500'
                          }`}
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                      {file.error && (
                        <p className="text-red-400 text-sm mt-1">{file.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Share Tab */}
        {activeTab === 'share' && selectedFolder && (
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">
                Share Files from: {selectedFolder.client_name}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Recipient Email *
                  </label>
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    placeholder="client@example.com"
                    className="w-full px-4 py-2 bg-gray-900 rounded border border-gray-700 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Message (optional)
                  </label>
                  <textarea
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                    placeholder="Here are the files we discussed..."
                    className="w-full px-4 py-2 bg-gray-900 rounded border border-gray-700 text-white h-24"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Expires In
                    </label>
                    <select
                      value={shareExpiry}
                      onChange={(e) => setShareExpiry(Number(e.target.value))}
                      className="w-full px-4 py-2 bg-gray-900 rounded border border-gray-700 text-white"
                    >
                      <option value={1}>1 day</option>
                      <option value={3}>3 days</option>
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Password (optional)
                    </label>
                    <input
                      type="password"
                      value={sharePassword}
                      onChange={(e) => setSharePassword(e.target.value)}
                      placeholder="Optional password"
                      className="w-full px-4 py-2 bg-gray-900 rounded border border-gray-700 text-white"
                    />
                  </div>
                </div>

                <button
                  onClick={shareFiles}
                  disabled={!shareEmail || loading}
                  className="w-full bg-green-600 py-3 rounded font-bold hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Creating Share Link...' : '🔗 Create Share Link & Send Email'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};